import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Alert,
    Anchor,
    Button,
    CopyButton,
    Group,
    Modal,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconExternalLink, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { generateMonitoringLink } from '../../api/resources';
import {
    DEFAULT_EXPIRY_HOURS,
    EXPIRY_HOURS_OPTIONS,
    buildMonitoringLinkUrl,
    formatDateTime,
} from '../../lib/monitoringLinks';
import { preferApplicantSubject } from '../../lib/subjectUtils';
import type {
    CaseRecord,
    GeneratedMonitoringLink,
    MonitoringLinkListItem,
} from '../../types/api';

/**
 * Creates a monitoring check link for one customer on a case, then shows it once:
 * the API only ever returns the token at creation.
 */
type GenerateMonitoringLinkModalProps = {
    opened: boolean;
    onClose: () => void;
    caseRecord: CaseRecord;
    /** Links already on the case, to warn before replacing a live one. */
    existingLinks: MonitoringLinkListItem[];
};

export function GenerateMonitoringLinkModal(props: GenerateMonitoringLinkModalProps) {
    // Mounted only while open, so every opening starts from a clean form.
    return props.opened ? <GenerateMonitoringLinkDialog {...props} /> : null;
}

function GenerateMonitoringLinkDialog({
    onClose,
    caseRecord,
    existingLinks,
}: GenerateMonitoringLinkModalProps) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const subjects = caseRecord.caseSubjects ?? [];
    const pois = caseRecord.casePois ?? [];

    // The modal's content unmounts on close, so these start fresh each time:
    // the applicant, and the address when the case has just one.
    const [subjectId, setSubjectId] = useState<string | null>(
        () => preferApplicantSubject(subjects)?.subjectId ?? null,
    );
    const [poiId, setPoiId] = useState<string | null>(() =>
        pois.length === 1 ? pois[0].poiId : null,
    );
    const [expiryHours, setExpiryHours] = useState<string>(String(DEFAULT_EXPIRY_HOURS));
    const [generated, setGenerated] = useState<GeneratedMonitoringLink | null>(null);

    const generateMutation = useMutation({
        mutationFn: () =>
            generateMonitoringLink(caseRecord.id, {
                subjectId: subjectId!,
                ...(poiId ? { poiId } : {}),
                expiresInHours: Number(expiryHours),
            }),
        onSuccess: (link) => {
            setGenerated(link);
            void queryClient.invalidateQueries({ queryKey: ['monitoring-links'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    const subjectName = (id: string | null) =>
        subjects.find((s) => s.subjectId === id)?.subject?.fullName ?? t('entities.subject');

    const liveLink = existingLinks.find(
        (link) => link.subjectId === subjectId && link.lifecycle === 'live',
    );

    const linkUrl = generated ? buildMonitoringLinkUrl(generated.token) : null;

    return (
        <Modal
            opened
            onClose={onClose}
            title={
                generated
                    ? t('features.monitoringLinks.generatedTitle')
                    : t('features.monitoringLinks.generateTitle')
            }
            size="lg"
        >
            {generated ? (
                <Stack gap="md">
                    <Alert color="yellow" icon={<IconInfoCircle size={18} />}>
                        {t('features.monitoringLinks.generatedOnce')}
                    </Alert>

                    {linkUrl ? (
                        <TextInput
                            label={t('features.monitoringLinks.linkLabel')}
                            value={linkUrl}
                            readOnly
                            onFocus={(e) => e.currentTarget.select()}
                            styles={{ input: { fontFamily: 'monospace' } }}
                        />
                    ) : (
                        <Stack gap="xs">
                            <TextInput
                                label={t('features.monitoringLinks.tokenLabel')}
                                value={generated.token}
                                readOnly
                                onFocus={(e) => e.currentTarget.select()}
                                styles={{ input: { fontFamily: 'monospace' } }}
                            />
                            <Text size="xs" c="dimmed">
                                {t('features.monitoringLinks.noBaseUrl')}
                            </Text>
                        </Stack>
                    )}

                    <Text size="sm" c="dimmed">
                        {subjectName(generated.subjectId)} ·{' '}
                        {t('features.monitoringLinks.expiresOn', {
                            when: formatDateTime(generated.expiresAt),
                        })}
                    </Text>

                    <Group justify="space-between">
                        <Group gap="sm">
                            <CopyButton value={linkUrl ?? generated.token}>
                                {({ copied, copy }) => (
                                    <Button
                                        color={copied ? 'teal' : undefined}
                                        leftSection={
                                            copied ? <IconCheck size={16} /> : <IconCopy size={16} />
                                        }
                                        onClick={copy}
                                    >
                                        {copied
                                            ? t('common.copy.copied')
                                            : t('features.monitoringLinks.copyLink')}
                                    </Button>
                                )}
                            </CopyButton>
                            {linkUrl ? (
                                <Anchor href={linkUrl} target="_blank" rel="noreferrer" size="sm">
                                    <Group gap={4}>
                                        <IconExternalLink size={15} />
                                        {t('features.monitoringLinks.openLink')}
                                    </Group>
                                </Anchor>
                            ) : null}
                        </Group>
                        <Button variant="default" onClick={onClose}>
                            {t('features.monitoringLinks.done')}
                        </Button>
                    </Group>
                </Stack>
            ) : (
                <Stack gap="md">
                    <Text size="sm" c="dimmed">
                        {t('features.monitoringLinks.generateHint')}
                    </Text>

                    {subjects.length === 0 ? (
                        <Alert color="gray">{t('features.monitoringLinks.noCustomers')}</Alert>
                    ) : (
                        <>
                            <Select
                                label={t('features.monitoringLinks.customer')}
                                data={subjects.map((link) => ({
                                    value: link.subjectId,
                                    label: link.role
                                        ? `${link.subject?.fullName ?? link.subjectId} (${link.role})`
                                        : (link.subject?.fullName ?? link.subjectId),
                                }))}
                                value={subjectId}
                                onChange={setSubjectId}
                                allowDeselect={false}
                                error={
                                    generateMutation.isError && !subjectId
                                        ? t('features.monitoringLinks.customerRequired')
                                        : undefined
                                }
                            />
                            <Select
                                label={t('features.monitoringLinks.address')}
                                description={t('features.monitoringLinks.addressHint')}
                                placeholder={t('features.monitoringLinks.noAddress')}
                                clearable
                                data={pois.map((link) => ({
                                    value: link.poiId,
                                    label:
                                        link.poi?.claimedAddress ??
                                        link.poi?.name ??
                                        link.poiId,
                                }))}
                                value={poiId}
                                onChange={setPoiId}
                            />
                            <Select
                                label={t('features.monitoringLinks.validFor')}
                                data={EXPIRY_HOURS_OPTIONS.map((hours) => ({
                                    value: String(hours),
                                    label: t('features.monitoringLinks.days', { count: hours / 24 }),
                                }))}
                                value={expiryHours}
                                onChange={(v) => v && setExpiryHours(v)}
                                allowDeselect={false}
                            />

                            {liveLink ? (
                                <Alert color="orange" icon={<IconInfoCircle size={18} />}>
                                    {t('features.monitoringLinks.replacesExisting', {
                                        name: subjectName(subjectId),
                                        when: formatDateTime(liveLink.createdAt),
                                    })}
                                </Alert>
                            ) : null}
                        </>
                    )}

                    <Group justify="flex-end">
                        <Button variant="default" onClick={onClose}>
                            {t('common.actions.cancel')}
                        </Button>
                        <Button
                            disabled={!subjectId}
                            loading={generateMutation.isPending}
                            onClick={() => generateMutation.mutate()}
                        >
                            {t('features.monitoringLinks.generateAction')}
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
}
